import { Fragment, useState, useRef, useEffect } from 'react'
import {
  BarChart3,
  Bell,
  BellOff,
  ChevronLeft,
  ChevronRight,
  Inbox,
  LineChart,
  LogOut,
  Phone,
  Rocket,
  Settings,
  Smartphone,
  User,
  Users,
  Zap,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/stores/useUIStore'
import { useAuthStore } from '@/stores/useAuthStore'
import { useProfile } from '@/api/profile'
import { useMiseEnRouteAFinir } from '@/hooks/useMiseEnRoute'
import { useAgentStatus, useToggleAgentStatus } from '@/api/agents'
import { logout } from '@/lib/auth'
import { Collegues } from './Collegues'
import { OutilsPerso } from './OutilsPerso'
import { usePlanRights } from '@/hooks/usePlanRights'
import { useInboxLines } from '@/api/inbox'
import { LignesBoite } from './LignesBoite'
import { CarteEssai } from '@/components/CarteEssai'
import { useAccess } from '@/hooks/useAccess'

interface NavItem {
  id: string
  icon: typeof Phone
  label: string
  badge?: { text: string; tone: 'orange' | 'violet' }
}

interface Props {
  activeTab: string
  onTabChange: (tab: string) => void
  /** Conversations non lues de la Boîte de réception. */
  unreadInboxCount?: number
}

export function DesktopSidebar({
  activeTab,
  onTabChange,
  unreadInboxCount = 0,
}: Props) {
  // Une entrée par ligne sous la Boîte de réception, dès deux lignes.
  const lignes = useInboxLines()
  const lineFilter = useUIStore((s) => s.inboxLineFilter)
  const setInboxLineFilter = useUIStore((s) => s.setInboxLineFilter)
  const setSelectedInboxPhone = useUIStore((s) => s.setSelectedInboxPhone)
  const choisirLigne = (ligne: string | null) => {
    onTabChange('phone')
    if (ligne !== lineFilter) setSelectedInboxPhone(null)
    setInboxLineFilter(ligne)
  }
  const { t } = useTranslation()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const navigate = useNavigate()
  const { darkMode, sidebarCollapsed, toggleSidebarCollapsed, toggleDarkMode } = useUIStore()
  const { data: profile } = useProfile()
  const { data: agentStatus } = useAgentStatus()
  const toggleStatus = useToggleAgentStatus()

  const isManager = profile?.role === 'MANAGER'
  // A supervisor reaches live calls, analytics and history of the admin space.
  const access = useAccess()
  // Pendant l'essai, le manager garde sous la main la mise en route de l'administration, jusqu'à ce qu'elle soit finie.
  const commencer = useMiseEnRouteAFinir() === true
  const isOffline = agentStatus === 'OFFLINE'
  // Ce que le plan n'inclut pas n'apparaît pas dans l'espace agent : ce n'est
  // pas lui qui choisit le plan, on ne lui montre pas de cadenas.
  const { allowed } = usePlanRights()
  const navItems: NavItem[] = [
    ...(commencer ? [{ id: 'start', icon: Rocket, label: t('manager.tabs.start', 'Commencer') } as NavItem] : []),
    { id: 'phone', icon: Inbox, label: t('sidebar.inbox', 'Boîte de réception') },
    { id: 'conversations', icon: Users, label: t('sidebar.contacts', 'Contacts') },
    ...(allowed('power_dialer')
      ? [{ id: 'power-dialer', icon: Zap, label: 'Power Dialer' } as NavItem]
      : []),
    { id: 'analytics', icon: BarChart3, label: t('sidebar.analytics', 'Analytics') },
  ]

  const sidebarBg = darkMode ? '#1e293b' : '#ffffff'
  const borderColor = darkMode ? 'rgb(30, 41, 59)' : 'rgb(226, 232, 240)'

  return (
    <aside
      className={cn(
        'hidden md:flex h-screen flex-col border-r transition-all duration-200',
        sidebarCollapsed ? 'w-[68px]' : 'w-64',
        darkMode ? 'text-slate-300' : 'text-slate-700',
      )}
      style={{ backgroundColor: sidebarBg, borderColor }}
    >
      {/* Header */}
      {sidebarCollapsed ? (
        <div className="p-3 pb-2 flex flex-col items-center gap-2">
          <div
            className={cn(
              'w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0',
              darkMode ? 'bg-white' : 'bg-slate-900',
            )}
          >
            <img
              src="https://sdk.callizy.ca/assets/favicon_callizy.png"
              alt="Callizy"
              className="object-contain w-full h-full"
            />
          </div>
          <button
            type="button"
            onClick={toggleSidebarCollapsed}
            title={t('sidebar.expand', 'Déplier la barre latérale')}
            className={cn(
              'w-7 h-7 rounded-md flex items-center justify-center transition-colors flex-shrink-0',
              darkMode
                ? 'bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white'
                : 'bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-900',
            )}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-3 p-6 pb-2">
          <div
            className={cn(
              'w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0',
              darkMode ? 'bg-white' : 'bg-slate-900',
            )}
          >
            <img
              src="https://sdk.callizy.ca/assets/favicon_callizy.png"
              alt="Callizy"
              className="object-contain w-full h-full"
            />
          </div>
          <span
            className={cn(
              'font-bold text-xl tracking-tight flex-1',
              darkMode ? 'text-white' : 'text-slate-900',
            )}
          >
            Callizy
          </span>
          <button
            type="button"
            onClick={toggleSidebarCollapsed}
            title={t('sidebar.collapse', 'Replier la barre latérale')}
            className={cn(
              'p-1.5 rounded-lg transition-colors',
              darkMode
                ? 'text-slate-400 hover:text-white hover:bg-slate-800'
                : 'text-slate-400 hover:text-slate-900 hover:bg-slate-100',
            )}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const Icon = item.icon
          const isActive = activeTab === item.id
          return (
            <Fragment key={item.id}>
            <button
              type="button"
              onClick={() =>
                item.id === 'start'
                  ? navigate(`/${locale}/manager`)
                  : item.id === 'phone'
                    ? choisirLigne(null)
                    : onTabChange(item.id)
              }
              className={cn(
                'w-full flex items-center py-3 rounded-xl text-sm font-medium transition-colors',
                sidebarCollapsed ? 'justify-center px-2' : 'gap-3 px-4',
                isActive
                  ? darkMode
                    ? 'bg-slate-800 text-white'
                    : 'bg-slate-100 text-slate-900'
                  : darkMode
                    ? 'text-slate-300 hover:bg-slate-800 hover:text-white'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900',
              )}
              title={sidebarCollapsed ? item.label : undefined}
            >
              <div className="relative flex-shrink-0">
                <Icon className="h-5 w-5" />
                {/* Pastille en mode replié pour signaler les non lus */}
                {sidebarCollapsed && item.id === 'phone' && unreadInboxCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[14px] h-3.5 px-1 flex items-center justify-center text-[8px] font-bold bg-indigo-600 text-white rounded-full">
                    {unreadInboxCount > 9 ? '9+' : unreadInboxCount}
                  </span>
                )}
              </div>
              {!sidebarCollapsed && (
                <div className="flex items-center gap-2 flex-1">
                  <span className="flex-1 text-left">{item.label}</span>
                  {item.id === 'phone' && unreadInboxCount > 0 && (
                    <span className="px-1.5 py-0.5 text-[10px] leading-none font-bold bg-indigo-600 text-white rounded-full">
                      {unreadInboxCount}
                    </span>
                  )}
                  {item.badge && (
                    <span
                      className={cn(
                        'px-1.5 py-0.5 text-[10px] leading-none font-bold text-white rounded-full',
                        item.badge.tone === 'orange'
                          ? 'bg-orange-500'
                          : 'bg-violet-500',
                      )}
                    >
                      {item.badge.text}
                    </span>
                  )}
                </div>
              )}
            </button>
            {item.id === 'phone' && !sidebarCollapsed && lignes.length > 1 && (
              <LignesBoite
                lignes={lignes}
                active={isActive}
                lineFilter={lineFilter}
                unreadTotal={unreadInboxCount}
                onChoose={choisirLigne}
                darkMode={darkMode}
              />
            )}
            </Fragment>
          )
        })}
      </nav>

      {/* Collègues : présence et messages entre collègues, à la place de la page Équipe */}
      <Collegues darkMode={darkMode} collapsed={sidebarCollapsed} actif={activeTab === 'phone'} />

      {isManager && <CarteEssai darkMode={darkMode} collapsed={sidebarCollapsed} className="mx-3 mt-2 mb-1" />}

      {/* Footer */}
      <div
        className={cn(
          'p-4 border-t space-y-1',
          darkMode ? 'border-slate-800' : 'border-slate-200',
        )}
      >
        {/* Configuration → réservé aux MANAGER, bascule vers /manager */}
        {isManager && (
          <button
            type="button"
            onClick={() => navigate(`/${locale}/manager`)}
            title={t('sidebar.configuration', 'Administration')}
            className={cn(
              'w-full flex items-center py-2 rounded-xl text-sm transition-colors',
              sidebarCollapsed ? 'justify-center px-2' : 'gap-3 px-4',
              darkMode
                ? 'text-slate-300 hover:bg-slate-800 hover:text-white'
                : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900',
            )}
          >
            <Settings className="h-5 w-5" />
            {!sidebarCollapsed && (
              <span>{t('sidebar.configuration', 'Administration')}</span>
            )}
          </button>
        )}
        {!isManager && access.isSupervisor && (
          <button
            type="button"
            onClick={() => navigate(`/${locale}/manager?view=analytics`)}
            title={t('recordingAccess.teamInsights', 'Suivi de l’équipe')}
            className={cn(
              'w-full flex items-center py-2 rounded-xl text-sm transition-colors',
              sidebarCollapsed ? 'justify-center px-2' : 'gap-3 px-4',
              darkMode
                ? 'text-slate-300 hover:bg-slate-800 hover:text-white'
                : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900',
            )}
          >
            <LineChart className="h-5 w-5" />
            {!sidebarCollapsed && (
              <span>{t('recordingAccess.teamInsights', 'Suivi de l’équipe')}</span>
            )}
          </button>
        )}

        {/* Moi : statut, profil, thème, langue, audio, déconnexion, sous une seule entrée (modèle Aircall) */}
        <ProfilePopover
          collapsed={sidebarCollapsed}
          firstName={profile?.first_name || ''}
          lastName={profile?.last_name || ''}
          role={profile?.role || ''}
          roleLabel={profile?.access ? t(`recordingAccess.roles.${profile.access.role}.label`) : undefined}
          darkMode={darkMode}
          isOffline={isOffline}
          onToggleStatus={() => agentStatus && toggleStatus.mutate(agentStatus)}
          onNavigateToProfile={() => onTabChange('profile')}
        />
        <OutilsPerso darkMode={darkMode} collapsed={sidebarCollapsed} onToggleDarkMode={toggleDarkMode} />
      </div>
    </aside>
  )
}

interface ProfilePopoverProps {
  collapsed: boolean
  firstName: string
  lastName: string
  role: string
  /** Translated role (Owner, Manager, Supervisor, Member) when the server sends it. */
  roleLabel?: string
  darkMode: boolean
  isOffline: boolean
  onToggleStatus: () => void
  onNavigateToProfile?: () => void
}

function ProfilePopover({
  collapsed,
  firstName,
  lastName,
  role,
  roleLabel,
  darkMode,
  isOffline,
  onToggleStatus,
  onNavigateToProfile,
}: ProfilePopoverProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()
  const { locale = 'fr' } = useParams<{ locale: string }>()

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const initials = `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase() || 'U'

  const avatar = (
    <span className="relative flex-shrink-0">
      <span
        className={cn(
          'w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold',
          darkMode ? 'bg-slate-700 text-white' : 'bg-[#0A192F] text-white',
        )}
      >
        {initials}
      </span>
      <span className={cn('absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2', darkMode ? 'ring-slate-900' : 'ring-white', isOffline ? 'bg-red-500' : 'bg-emerald-500')} />
    </span>
  )

  return (
    <div className="relative mt-1" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={collapsed ? `${firstName} ${lastName}` : undefined}
        className={cn(
          'w-full flex items-center rounded-xl text-sm font-medium transition-colors',
          collapsed ? 'justify-center py-2' : 'gap-3 px-3 py-2',
          darkMode ? 'hover:bg-slate-800 text-white' : 'hover:bg-slate-50 text-slate-900',
        )}
      >
        {avatar}
        {!collapsed && (
          <div className="flex-1 text-left min-w-0">
            <p className="truncate text-sm font-semibold">
              {firstName} {lastName}
            </p>
            <p className={cn('truncate text-[11px]', isOffline ? 'text-red-500' : 'text-emerald-600 dark:text-emerald-400')}>
              {isOffline ? t('sidebar.dnd', 'Ne pas déranger') : t('sidebar.available', 'Disponible')}
              <span className={cn('ml-1', darkMode ? 'text-slate-500' : 'text-slate-400')}>· {roleLabel ?? (role === 'MANAGER' ? 'Manager' : 'Agent')}</span>
            </p>
          </div>
        )}
      </button>

      {open && (
        <div
          className={cn(
            'absolute bottom-full left-0 right-0 mb-1 rounded-xl border shadow-lg overflow-hidden z-50',
            darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200',
          )}
        >
          {/* Statut */}
          <button
            type="button"
            onClick={onToggleStatus}
            className={cn(
              'w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors',
              darkMode ? 'text-slate-200 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50',
            )}
          >
            {isOffline ? <BellOff className="w-4 h-4 text-red-500" /> : <Bell className="w-4 h-4 text-emerald-500" />}
            <span className="flex-1 text-left">{t('sidebar.dnd', 'Ne pas déranger')}</span>
            <span className={cn('relative inline-flex h-4 w-7 rounded-full transition-colors', isOffline ? 'bg-red-500' : darkMode ? 'bg-slate-600' : 'bg-slate-300')}>
              <span className={cn('absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all', isOffline ? 'left-3.5' : 'left-0.5')} />
            </span>
          </button>
          <div className={cn('h-px', darkMode ? 'bg-slate-700' : 'bg-slate-100')} />
          <div className={cn('h-px', darkMode ? 'bg-slate-700' : 'bg-slate-100')} />
          <button
            type="button"
            onClick={() => {
              setOpen(false)
              onNavigateToProfile?.()
            }}
            className={cn(
              'w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors',
              darkMode
                ? 'text-slate-200 hover:bg-slate-700'
                : 'text-slate-700 hover:bg-slate-50',
            )}
          >
            <User className="w-4 h-4" />
            <span>{t('sidebar.myProfile', 'Mon profil')}</span>
          </button>
          <a
            href={`https://callizy.ca/${locale}/app`}
            target="_blank"
            rel="noreferrer"
            onClick={() => setOpen(false)}
            className={cn(
              'w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors',
              darkMode ? 'text-slate-200 hover:bg-slate-700' : 'text-slate-700 hover:bg-slate-50',
            )}
          >
            <Smartphone className="w-4 h-4" />
            <span>{t('sidebar.getApp', "Télécharger l'app")}</span>
          </a>
          <div className={cn('h-px', darkMode ? 'bg-slate-700' : 'bg-slate-100')} />
          <button
            type="button"
            onClick={() => {
              setOpen(false)
              const accessToken = useAuthStore.getState().accessToken
              if (accessToken) logout()
              else logout()
            }}
            className={cn(
              'w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors',
              darkMode
                ? 'text-rose-400 hover:bg-rose-500/10'
                : 'text-rose-500 hover:bg-rose-50',
            )}
          >
            <LogOut className="w-4 h-4" />
            <span>{t('sidebar.logout', 'Déconnexion')}</span>
          </button>
        </div>
      )}
    </div>
  )
}
